/**
 * User Activity Detection Utility
 * 
 * Tracks user activity to enable smart token refresh logic.
 * Only refreshes tokens for active users to prevent unnecessary API calls.
 */

class ActivityDetector {
  constructor() {
    this.lastActivity = Date.now();
    this.activityThreshold = 15 * 60 * 1000; // 15 minutes
    this.isMonitoring = false;
    this.activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  }

  /**
   * Start monitoring user activity
   */
  startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.lastActivity = Date.now();
    
    // Add event listeners for various user interactions
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.handleActivity, true);
    });
    
    console.log('🔍 Activity monitoring started');
  }

  /**
   * Stop monitoring user activity
   */
  stopMonitoring() {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    
    // Remove event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true);
    });
    
    console.log('🔍 Activity monitoring stopped');
  }

  /**
   * Handle user activity events
   */
  handleActivity = () => {
    this.lastActivity = Date.now();
    
    // Notify token manager about user activity (for immediate refresh check)
    if (this.onActivity) {
      this.onActivity();
    }
  }

  /**
   * Set callback for activity events
   */
  setActivityCallback(callback) {
    this.onActivity = callback;
  }

  /**
   * Check if user has been active recently
   * @param {number} thresholdMs - Activity threshold in milliseconds (optional)
   * @returns {boolean} True if user was active within threshold
   */
  isUserActive(thresholdMs = this.activityThreshold) {
    const timeSinceActivity = Date.now() - this.lastActivity;
    return timeSinceActivity < thresholdMs;
  }

  /**
   * Get time since last activity
   * @returns {number} Milliseconds since last activity
   */
  getTimeSinceLastActivity() {
    return Date.now() - this.lastActivity;
  }

  /**
   * Get last activity timestamp
   * @returns {number} Timestamp of last activity
   */
  getLastActivityTime() {
    return this.lastActivity;
  }

  /**
   * Reset activity timer (useful for testing or manual reset)
   */
  resetActivity() {
    this.lastActivity = Date.now();
  }
}

// Export singleton instance
export default new ActivityDetector();
